// Strategy View - strategy selection screen with featured and secondary cards
// Receives all data from ViewModel via props, no business logic

import { Dispatchers } from '@client/new/helpers/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { StrategyVM, CaseStudyVM } from '../view-model/Strategy.viewModel';
import { OnboardingVMActionMap } from '../view-model/Onboarding.viewModel';
import { FeaturedStrategyCard } from './components/FeaturedStrategyCard';
import { StrategyCard } from './components/StrategyCard';
import * as styles from './Strategy.view.css';

export type StrategyViewProps = {
  strategies: StrategyVM[];
  caseStudies: CaseStudyVM[];
  isSaving: boolean;
  isReviewMode: boolean;
} & Dispatchers<OnboardingVMActionMap>;

export const StrategyView = ({
  strategies,
  caseStudies: _caseStudies,
  isSaving,
  isReviewMode,
  dispatch,
  lazyDispatch,
}: StrategyViewProps) => {
  const handleSelectStrategy = (strategyId: string): void => {
    dispatch('SelectStrategy', strategyId);
  };

  const featuredStrategy = strategies.find((s) => s.isRecommended) ?? null;
  const secondaryStrategies = featuredStrategy
    ? strategies.filter((s) => s.id !== featuredStrategy.id)
    : strategies;

  return (
    <div className={styles.container} data-testid="strategy-view">
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <PlaybookLogo showGlyph showName height={24} color="N-100" />
          {isReviewMode && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={lazyDispatch('CloseReview')}
              disabled={isSaving}
              data-testid="close-button"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Title Section */}
        <section className={styles.titleSection}>
          <h1 className={styles.mainTitle}>
            Pick your winning strategy to start with
          </h1>
          <p className={styles.mainSubtitle}>
            Best strategies we crafted for you based on our experience with
            1000+ creators like you
          </p>
        </section>

        {/* Strategies Section */}
        <section className={styles.strategiesSection}>
          {/* Featured Strategy (Lead Magnet) */}
          {featuredStrategy && (
            <div className={styles.featuredSection}>
              <span className={styles.recommendedBadge}>RECOMMENDED</span>
              <div
                className={styles.featuredCardWrapper}
                style={{ animationDelay: '0s' }}
              >
                <FeaturedStrategyCard
                  strategy={featuredStrategy}
                  caseStudy={featuredStrategy.caseStudies[0] ?? null}
                  onSelect={handleSelectStrategy}
                />
              </div>
            </div>
          )}

          {/* Secondary Strategies Grid (3 columns) */}
          {secondaryStrategies.length > 0 && (
            <div className={styles.secondaryStrategiesGrid}>
              {secondaryStrategies.map((strategy, index) => (
                <div
                  key={strategy.id}
                  className={styles.strategyCardWrapper}
                  style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                >
                  <StrategyCard
                    strategy={strategy}
                    caseStudy={strategy.caseStudies[0] ?? null}
                    onSelect={handleSelectStrategy}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer with Dismiss/Clear Strategy Option */}
      <footer className={styles.footer}>
        {isReviewMode ? (
          <>
            <span className={styles.dismissText}>
              Don&apos;t feel like selecting one right now? No problem, you can
              choose one later.
            </span>
            <button
              type="button"
              className={styles.dismissButton}
              onClick={lazyDispatch('ClearStrategy')}
              disabled={isSaving}
              data-testid="clear-strategy-button"
            >
              Clear strategy
              <FontAwesomeIcon icon={faClose} className={styles.dismissIcon} />
            </button>
          </>
        ) : (
          <>
            <span className={styles.dismissText}>
              Don&apos;t feel like selecting strategy right now? No problem, you
              can choose it later.
            </span>
            <button
              type="button"
              className={styles.dismissButton}
              onClick={lazyDispatch('Dismiss')}
              disabled={isSaving}
              data-testid="dismiss-button"
            >
              Dismiss
              <FontAwesomeIcon icon={faClose} className={styles.dismissIcon} />
            </button>
          </>
        )}
      </footer>

      {/* Saving Overlay */}
      {isSaving && (
        <div className={styles.savingOverlay} data-testid="saving-overlay">
          <span className={styles.savingText}>Saving...</span>
        </div>
      )}
    </div>
  );
};
